/**
 * SeoContentPage — activiteitenlogboek van de SEO/content-automatisering.
 *
 * Leeslaag op marketing_activity_log (migratie 024): elke run van de
 * /content-, /seo- en /linkbuilding-skills (cron of sessie) logt hier wat er
 * gedaan is en waarom. Per regel het meest recente meetverdict uit
 * seo_experiments, zodat toezicht ook toont óf het werkte.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { MarketingPageHeader, SectionCard, StatTile, formatDateTime } from './shared';

interface ActivityRow {
  id: number;
  ts: string;
  skill: string;
  mode: string | null;
  actor: string;
  brand: string | null;
  action: string;
  title: string | null;
  url: string | null;
  keyword: string | null;
  hypothesis: string | null;
  commit_sha: string | null;
  status: 'done' | 'failed' | 'skipped';
  detail: Record<string, unknown> | null;
  experiment_result: string | null;
  experiment_delta_pct: number | null;
  experiment_position_delta: number | null;
  experiment_measured_date: string | null;
}

const SKILL_FILTERS = ['alle', 'content', 'seo', 'linkbuilding'] as const;

const STATUS_STYLE: Record<ActivityRow['status'], string> = {
  done: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-700',
  skipped: 'bg-amber-50 text-amber-700',
};

/**
 * `failed` betekent twee verschillende dingen in het logboek en dat leest verwarrend:
 * bij de controle-skill draaide de run prima maar zákt de gecontroleerde pagina
 * (bevindingen gevonden — dat is de agent die zijn werk doet), bij de andere skills
 * is de run zelf mislukt. Toon daarom een apart label per betekenis.
 */
function statusLabel(row: ActivityRow): { text: string; cls: string; title: string } {
  if (row.status === 'failed' && row.skill === 'controle') {
    return {
      text: 'bevindingen',
      cls: 'bg-orange-50 text-orange-700',
      title: 'De controle-run is goed verlopen; de gecontroleerde pagina heeft openstaande bevindingen.',
    };
  }
  const label: Record<ActivityRow['status'], string> = {
    done: 'done',
    failed: 'mislukt',
    skipped: 'skipped',
  };
  return { text: label[row.status], cls: STATUS_STYLE[row.status], title: '' };
}

const VERDICT_STYLE: Record<string, string> = {
  improved: 'bg-green-50 text-green-700',
  regressed: 'bg-red-50 text-red-700',
  no_change: 'bg-gray-100 text-gray-600',
  insufficient_data: 'bg-gray-100 text-gray-500',
};

function VerdictBadge({ row }: { row: ActivityRow }) {
  if (!row.experiment_result) {
    return row.url ? <span className="text-xs text-gray-400">meting volgt</span> : null;
  }
  const delta =
    row.experiment_delta_pct != null ? ` ${row.experiment_delta_pct > 0 ? '+' : ''}${Math.round(row.experiment_delta_pct)}%` : '';
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${VERDICT_STYLE[row.experiment_result] ?? 'bg-gray-100 text-gray-600'}`}
      title={row.experiment_measured_date ? `Gemeten ${row.experiment_measured_date}` : undefined}
    >
      {row.experiment_result}
      {delta}
    </span>
  );
}

export function SeoContentPage() {
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [skill, setSkill] = useState<(typeof SKILL_FILTERS)[number]>('alle');
  const [days, setDays] = useState(60);

  const fetchLog = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Niet ingelogd');
      const params = new URLSearchParams({ days: String(days) });
      if (skill !== 'alle') params.set('skill', skill);
      const res = await fetch(`/api/marketing/activity-log?${params}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.error || 'Kon logboek niet laden');
      }
      const payload = (await res.json()) as { items?: ActivityRow[] };
      setRows(Array.isArray(payload.items) ? payload.items : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kon logboek niet laden');
    } finally {
      setIsLoading(false);
    }
  }, [skill, days]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  const published = rows.filter((r) => r.action === 'content_published' && r.status === 'done').length;
  // Controle-runs met status 'failed' zijn geen mislukte runs maar pagina's met
  // openstaande bevindingen — apart tellen, anders leest de tegel als storing.
  const failed = rows.filter((r) => r.status === 'failed' && r.skill !== 'controle').length;
  const withFindings = rows.filter((r) => r.status === 'failed' && r.skill === 'controle').length;
  const cronRuns = rows.filter((r) => r.actor === 'claude-cron').length;
  const measured = rows.filter((r) => r.experiment_result && r.experiment_result !== 'insufficient_data').length;

  return (
    <div className="flex-1 overflow-y-auto">
      <MarketingPageHeader
        title="SEO & Content"
        subtitle="Wat de automatisering deed en waarom — met het meetverdict erbij zodra dat er is"
      />

      <div className="p-6 space-y-6">
        {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile label={`Runs (${days}d)`} value={rows.length.toLocaleString('nl-NL')} sub={`${cronRuns} via cron`} />
          <StatTile label="Gepubliceerd" value={published.toLocaleString('nl-NL')} />
          <StatTile
            label="Mislukt"
            value={failed.toLocaleString('nl-NL')}
            sub={withFindings ? `${withFindings} pagina's met bevindingen (apart)` : undefined}
          />
          <StatTile label="Met meetverdict" value={measured.toLocaleString('nl-NL')} />
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1">
            {SKILL_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => setSkill(s)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  skill === s ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-600"
          >
            <option value={30}>30 dagen</option>
            <option value={60}>60 dagen</option>
            <option value={180}>180 dagen</option>
          </select>
        </div>

        <SectionCard title="Logboek">
          {isLoading && rows.length === 0 ? (
            <p className="text-sm text-gray-400">Laden…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-400">Nog geen activiteit in deze periode.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                    <th className="py-2 pr-4 font-medium">Wanneer</th>
                    <th className="py-2 pr-4 font-medium">Skill</th>
                    <th className="py-2 pr-4 font-medium">Merk</th>
                    <th className="py-2 pr-4 font-medium">Wat</th>
                    <th className="py-2 pr-4 font-medium">Waarom</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 font-medium">Resultaat</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b border-gray-50 align-top">
                      <td className="py-2.5 pr-4 whitespace-nowrap text-gray-500">
                        {formatDateTime(row.ts)}
                        {row.actor === 'claude-cron' && (
                          <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 text-[10px] font-medium">
                            cron
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4 whitespace-nowrap text-gray-700">
                        {row.skill}
                        {row.mode ? <span className="text-gray-400"> · {row.mode}</span> : null}
                      </td>
                      <td className="py-2.5 pr-4 whitespace-nowrap text-gray-700">{row.brand ?? '—'}</td>
                      <td className="py-2.5 pr-4 max-w-xs">
                        <span className="text-gray-400 text-xs">{row.action}</span>
                        <div className="text-gray-900">
                          {row.url ? (
                            <a href={row.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">
                              {row.title ?? row.url}
                            </a>
                          ) : (
                            row.title ?? '—'
                          )}
                        </div>
                        {row.keyword && <div className="text-xs text-gray-400">"{row.keyword}"</div>}
                      </td>
                      <td className="py-2.5 pr-4 max-w-md text-gray-600">{row.hypothesis ?? '—'}</td>
                      <td className="py-2.5 pr-4">
                        {(() => {
                          const s = statusLabel(row);
                          return (
                            <span
                              className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${s.cls}`}
                              title={s.title || undefined}
                            >
                              {s.text}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-2.5">
                        <VerdictBadge row={row} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
